import { useContext } from 'react';
import { MiniPDPContext } from '@/components/mini-pdp-context/MiniPDPContextProvider';
import { trackSelectSizeClicked } from '@/analytics/mini-pdp/shape-mini-pdp-track';

/**
 * Item type constants
 * @enum {string}
 */
export const ItemTypes = {
  /** Cart item type */
  CART: 'cart',
  /** Wishlist item type */
  WISHLIST: 'wishlist',
};

/**
 * Hook to manage the state and interactions for the size selection modal.
 * @param {Object} config Configuration object for the hook.
 * @param {ItemTypes} config.itemType The type of the item (either {@link ItemTypes.CART} or {@link ItemTypes.WISHLIST}).
 * @param {string} [config.gtin] GTIN identifier for the current item (canonical SKU identifier across cart and wishlist flows).
 * @returns {Object} The state and handlers for the size selection modal.
 *
 */

export default function useSizeSelectModal(
  { itemType: selectedItemType, gtin } = { itemType: '' },
) {
  const [
    {
      productCode,
      selectedGtin,
      itemType,
      itemId,
      isItemUpdating,
      loadWithError,
      isOpen,
      openModal,
      closeModal,
    },
    setItemState,
  ] = useContext(MiniPDPContext);

  /**
   * Function to open the size selection modal.
   * @param {Object} params Parameters for opening the modal.
   * @param {string} params.itemId The ID of the item.
   * @param {boolean} params.loadWithError Indicates if the modal should load with an error state showing.
   * @param {string} params.productCode The code or style color of the product (canonical hydration primary key).
   * @param {string} [params.productId] Cart/wishlist line-item UUID. Forwarded to
   *   `trackSelectSizeClicked` only as a cold-cache analytics fallback — the modal
   *   itself does not key off `productId`.
   * @param {string} params.selectedGtin The GTIN of the size currently associated with the item.
   * @returns {Promise<void>} A promise that resolves when the modal is opened.
   */
  const handleOpen = async ({
    itemId,
    loadWithError,
    productCode,
    productId,
    selectedGtin,
    skipSelectSizeTracking = false,
  }) => {
    if (!skipSelectSizeTracking) {
      if (selectedItemType === ItemTypes.CART) {
        const clickableItemEnums =
          await window.webShellClient.analytics.utils.getProtoEnums('Cart_ClickableItem_v2');
        window.webShellClient.analytics.trackAction(
          {
            underlyingAction: {
              case: 'uxCommerceCartV2ItemClicked',
              value: { item: clickableItemEnums['SIZE_SELECTOR_LINK'] },
            },
          },
          { eventVersion: 2 },
        );
      }

      trackSelectSizeClicked({ productId, productCode });
    }

    // clear out stale id from previous modal open here to prevent jank on modal close
    setItemState(prevState => ({
      ...prevState,
      selectedGtin: '',
      productCode: '',
    }));

    openModal();

    return setItemState(prevState => ({
      ...prevState,
      selectedGtin, // set for visual consistency for selection in grid when modal opens
      productCode,
      itemId,
      itemType: selectedItemType,
      loadWithError, // set true if you want to open the modal with the error state showing
    }));
  };
  const handleClose = () => {
    closeModal();

    setItemState(prevState => ({
      ...prevState,
      itemType: '',
    }));
  };

  // this block can be removed once we've moved ShopClientProvider to useMutation, where it tracks loading state
  const handleItemUpdating = () => {
    setItemState(prevState => ({
      ...prevState,
      isItemUpdating: true,
    }));
  };

  const handleItemUpdated = () => {
    setItemState(prevState => ({
      ...prevState,
      isItemUpdating: false,
    }));
  };
  // end block

  const isCurrentItemUpdating = Boolean(gtin) && gtin === selectedGtin;

  return {
    isOpen,
    itemType,
    productCode,
    selectedGtin,
    itemId,
    isItemUpdating: isItemUpdating && isCurrentItemUpdating,
    loadWithError,
    handleOpen,
    handleClose,
    handleItemUpdating,
    handleItemUpdated,
    setItemState: ({ gtin }) =>
      setItemState(prevState => ({
        ...prevState,
        selectedGtin: gtin,
        productCode: '',
      })),
  };
}
